import { ExportModalMode } from "./ExportModal";
import type { FieldData as DocumentFieldData } from "./ExportModal/DocumentExport";
import type { FieldData as ExcelFieldData } from "./ExportModal/ExcelExport";
import type { FieldData as ImageFieldData } from "./ExportModal/ImageExport";
import { GetWSMgr } from "@/Utils/StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { QXWSMgr } from "@coreapp/CoreApp/QXWSMgr";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QPublishSettings } from "@coreapp/CoreApp/QPublishSettings";
import { QXDocMgr } from "@coreapp/CoreApp/QXDocMgr";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { QPlanManager } from "@coreapp/CoreApp/QPlanManager";
import { enumBGOption, enumPanelRenderMode, enumSaveScope } from "@coreapp/constants";
import { GetFunctionalStatementFieldDef } from "@coreapp/utils/GetFunctionalStatementFieldDef";
import { yieldToPaint } from "@coreapp/utils/yieldToPaint";
import { ApplyChartTemplateToDocMgr } from "@coreapp/utils/ApplyChartTemplateToDocMgr";

type ExcelExportOptions = {
  type: "excel";
  isTableExport: boolean;
  useServerExport?: boolean;
  fieldData: ExcelFieldData;
  filterObj: any;
  results: any[];
};

type ImageExportOptions = {
  type: "image";
  mode: ExportModalMode;
  fieldData: ImageFieldData;
};

type DocumentExportOptions = {
  type: "document";
  mode: ExportModalMode;
  fieldData: DocumentFieldData;
};

type ExportOptions = ExcelExportOptions | ImageExportOptions | DocumentExportOptions;

export class Exporter {
  private wsMgr: QXWSMgr | null = null;

  constructor() {
    this.wsMgr = GetWSMgr();
  }

  private resolve: (value: boolean) => void = () => {};
  private reject: (value?: unknown) => void = () => {};

  export(options: ExportOptions) {
    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      switch (options.type) {
        case "image":
          this.exportImage(options);
          break;
        case "excel":
          this.exportExcel(options);
          break;
        case "document":
          this.exportDocument(options);
          break;
      }
    });
  }

  async exportImage(options: ImageExportOptions) {
    const wsMgr = this.wsMgr;
    if (isNullish(wsMgr)) {
      return;
    }

    const { fieldData } = options;

    // Write pub settings
    Globals._pubSettings = new QPublishSettings();
    Globals._pubSettings.fCropToChart = fieldData.cropToChart;
    Globals._pubSettings.fTransparentBackground = fieldData.transparentBackground;
    Globals._pubSettings.fWhiteBackground = fieldData.whiteBackground;
    Globals._pubSettings.fPNGQuality = fieldData.highQuality;

    // Perform export
    let pubType = "JPG";
    switch (options.mode) {
      case ExportModalMode.PNG:
        pubType = "PNG";
        if (Globals._pubSettings.fPNGQuality) {
          pubType = "HIRESPNG";
        }

        break;
      case ExportModalMode.SVG:
        pubType = "SVG";
        break;
    }

    const result = await wsMgr.PerformPublishCommand(pubType, "", fieldData.name);
    this.resolve(result);
  }

  async exportExcel(options: ExcelExportOptions) {
    const wsMgr = this.wsMgr;
    const doc = wsMgr?.GetDoc();
    if (isNullish(doc) || isNullish(wsMgr)) {
      this.reject();
      return;
    }

    const { fieldData, filterObj, results } = options;
    const { categoryFields } = fieldData;
    const positionExcluded = new Set(categoryFields.position.excluded);
    const employeeExcluded = new Set(categoryFields.employee.excluded);
    const positionFieldNames = categoryFields.position.included.filter(
      f => !positionExcluded.has(f)
    );
    let holderFieldNames = categoryFields.employee.included.filter(f => !employeeExcluded.has(f));
    let filteredFields = Array.from(new Set([...positionFieldNames, ...holderFieldNames]));

    if (
      fieldData.includeFunctionalStatements &&
      !!wsMgr.fPermissions?.showFunctionalStatement() &&
      !options.isTableExport
    ) {
      const fsFieldDef = GetFunctionalStatementFieldDef(doc.GetObjDB()?.GetClassByName("Box"));
      const fsFieldID = fsFieldDef?.GetFieldID();
      if (fsFieldID) {
        if (!holderFieldNames.includes(fsFieldID)) {
          holderFieldNames = [...holderFieldNames, fsFieldID];
        }
        if (!filteredFields.includes(fsFieldID)) {
          filteredFields = [...filteredFields, fsFieldID];
        }
      }
    }

    if (
      doc.IsServerChart() ||
      (doc.IsPlanChart() && options.isTableExport) ||
      options.useServerExport
    ) {
      await wsMgr.PerformExportToExcelDB(
        fieldData.scope,
        fieldData.name,
        positionFieldNames,
        holderFieldNames,
        fieldData.chainOfCommand,
        fieldData.systemCalculations,
        filterObj,
        fieldData.accessibilityReport
      );
    } else if (options.isTableExport) {
      await doc.DoExportDir(filteredFields, results, fieldData.name);
    } else {
      const boxClass = doc.GetBoxClass();
      const fieldDefs: QObjDBFieldDef[] = [];
      for (const fieldName of filteredFields) {
        const fieldDef =
          boxClass.GetFieldDefByName(fieldName) ?? boxClass.GetFieldDefByID(fieldName);
        if (isNullish(fieldDef)) {
          continue;
        }

        fieldDefs.push(fieldDef);
      }

      await wsMgr.PerformExportToExcel(
        fieldData.scope,
        fieldData.name,
        fieldDefs,
        fieldData.chainOfCommand,
        fieldData.systemCalculations
      );
    }

    this.resolve(true);
  }

  suspendPlanPolling(planMgr: QPlanManager | null) {
    const pollingTimeout = planMgr?.fPollListener;
    if (!pollingTimeout) {
      return () => undefined;
    }

    clearTimeout(pollingTimeout);
    return () => {
      planMgr.ActivateListener();
    };
  }

  createMockDocument(
    sourceDocument: QXDoc,
    abortController: AbortController,
    options?: { levels?: number; topBoxID?: string; chainLevels?: number; forceTopBoxID?: string }
  ) {
    return new Promise<[Nullable<QXDoc>, () => void]>(async resolve => {
      const wsMgr = this.wsMgr;
      const curDoc = sourceDocument;
      const curDocMgr = curDoc.GetDocMgr();
      const curViewMgr = curDoc.GetViewMgr();
      if (isNullish(wsMgr) || isNullish(curDocMgr) || isNullish(curDoc) || isNullish(curViewMgr)) {
        resolve([null, () => {}]);
        return;
      }

      const curCanvasPanel = curDoc.GetCanvasRef();
      Globals._publishRef = { isPreview: true };

      if (wsMgr.fSidebarMode === "background") {
        wsMgr.SetSidebarTool("filters");
      }

      curViewMgr.fFieldList = curViewMgr.fFieldList ?? [];
      const curViewFields = curViewMgr.fFieldList;
      const curViewId = curDoc.GetActiveTemplate().GetTemplateID();

      const curPlanMgr = wsMgr.GetPlanMgr();
      const resumePlanPolling = this.suspendPlanPolling(curPlanMgr);

      const curShowOverlay = Globals._uiConfig.fShowOverlay;

      const curScm = curDoc.GetSubChartMgr();
      const curRootSc = curScm?.GetRootSubChart();
      let curTopChartDBObj = curRootSc?.GetTopDBObj();
      if (curPlanMgr) {
        const planTopObj = curPlanMgr.GetTopObjRef();
        if (planTopObj) {
          curTopChartDBObj = planTopObj;
        }
      }
      const curChartTopId = curTopChartDBObj?.GetObjectRecordID();

      const mockDocMgr = new QXDocMgr();
      mockDocMgr.InitDocMgr(wsMgr);
      mockDocMgr.SetFileName(curDocMgr.GetFileName());
      mockDocMgr.SetRefreshDate(curDocMgr.GetRefreshDate());
      mockDocMgr.SetRefreshTime(curDocMgr.GetRefreshTime());
      wsMgr.fDocMgr = mockDocMgr;
      let mockDoc: QXDoc;

      const iWizData = curDoc.GetIWizData();
      const isLinkedBackground = iWizData.GetMasterOption() === enumBGOption.linked;

      wsMgr.fBuilderInfo = {
        doctype: curDoc.IsPlanChart() ? "plan" : undefined,
        levels: options?.levels ?? -1,
        chainLevels: options?.chainLevels ?? 0,
        liveChart: false,
        topBoxID: options?.topBoxID ?? curChartTopId,
        forceTopBoxID: options?.forceTopBoxID ?? null,
        mtemplate: isLinkedBackground ? iWizData.GetMasterFile() : null,
        viewid: curViewId
      } as any;

      const teardownTooManyResultsHandler = wsMgr.SubscribeOnceToTopic("TooManyResults", () => {
        resolve([null, teardownMockDocument]);
        return;
      });

      // Temporarily suppress fShowActivity during document loading and background application
      // to prevent AddMasterPubMargin from firing in ApplyBackgroundToCanvas. Activity margins
      // are managed exclusively by applyPlanningActivity (preview) and PreFlightPDF (export).
      // Without this, a persisted fShowActivity="History" from a prior export causes
      // AddMasterPubMargin to inflate master margins by 420px, then AddDocPubMargin
      // saves the already-inflated value and adds another 420px (OCN5-4050).
      // Must be set BEFORE document loading because OpenPlan() triggers an internal
      // ApplyBackground call that would otherwise invoke AddMasterPubMargin.
      const savedShowActivity = Globals._pubSettings?.fShowActivity;
      const restoreShowActivity = () => {
        if (Globals._pubSettings && savedShowActivity !== undefined) {
          Globals._pubSettings.fShowActivity = savedShowActivity;
        }
      };
      if (Globals._pubSettings) {
        Globals._pubSettings.fShowActivity = "Off";
      }

      try {
        if (curDoc.IsPlanChart()) {
          const docPromise = wsMgr.WaitForTopic<QXDoc>("chartDocumentLoaded");

          const planId = curPlanMgr?.GetPlanID();
          wsMgr.fLoadPlanPreventClose = true;
          wsMgr.OpenPlan(planId, undefined);

          mockDoc = await docPromise;
          if (abortController.signal.aborted) {
            resolve([null, teardownMockDocument]);
            return;
          }

          const mockPlanMgr = wsMgr.GetPlanMgr();
          if (mockPlanMgr?.fPollListener) {
            clearTimeout(mockPlanMgr.fPollListener);
          }
        } else if (curDoc.IsServerChart()) {
          mockDoc = wsMgr.InitializeDoc({}, false, false, true, true);

          const chartId = curDoc.GetChartID();
          wsMgr.SubscribeOnceToTopic("chartMetadataLoaded", () => {
            const mockCanvasPanel = mockDoc.GetCanvasRef();
            mockCanvasPanel?.SetRenderMode(enumPanelRenderMode.all);
          });
          wsMgr.GetChartMetadata(mockDoc, chartId, {});

          await wsMgr.WaitForTopic("chartDocumentLoaded");
          if (abortController.signal.aborted) {
            resolve([null, teardownMockDocument]);
            return;
          }
        } else {
          // Yield to let the modal loading spinner paint before heavy sync work
          await yieldToPaint();

          const json: any[] = [];
          curDoc.SetSaveScope(enumSaveScope.entireChart);

          // Mark the document as not a live chart to ensure that the
          // Object DB is included in the JSON output
          const initialLiveChart = curDoc.IsLiveChart();
          initialLiveChart && iWizData.SetIsLiveChart(false);

          curDoc.OutputToJSON(json);
          mockDocMgr.fChartMetadata = iWizData.Clone();

          initialLiveChart && iWizData.SetIsLiveChart(true);

          // Yield between serialization and deserialization to keep spinner responsive
          await yieldToPaint();

          mockDoc = mockDocMgr.LoadDocument(json[0], false, true)!;
          wsMgr.AttachDocumentToUI(mockDoc, false);
        }

        mockDoc.fIsPreview = true;
        wsMgr.fBuilderInfo = null as any;

        const backgroundPromise = wsMgr.WaitForTopic("backgroundApplied");
        wsMgr.ApplyBackground();
        await backgroundPromise;
      } finally {
        // Restore fShowActivity after all ApplyBackground calls are complete
        // (both the internal one from OpenPlan and the explicit one above).
        // In try/finally so fShowActivity is never left stuck on "Off".
        restoreShowActivity();
      }
      const mockCanvasPanel = mockDoc.GetCanvasRef();

      if (abortController.signal.aborted || isNullish(mockCanvasPanel)) {
        resolve([null, teardownMockDocument]);
        return;
      }

      mockCanvasPanel.SetRenderMode(enumPanelRenderMode.all);

      const mockPageLayoutMgr = mockDoc.GetPageLayoutMgr();
      mockPageLayoutMgr?.SetFitToScreen(false);

      const mockViewMgr = mockDoc.GetViewMgr()!;
      mockViewMgr.fFieldList = curViewFields;
      mockViewMgr.HandleUpdateBox();

      mockCanvasPanel.SetShownState(true);

      // Apply Footer/Header Spacing
      const curPageMgr = curDocMgr.GetPageMgr();
      const mockPageMgr = mockDoc.GetPageMgr();
      mockPageMgr?.CloneVerticalMarginsFrom(curPageMgr);

      // Apply Filter
      const curFilterMgr = curDoc.GetFilterMgr()!;
      const mockFilterMgr = mockDoc.GetFilterMgr()!;
      mockFilterMgr.fFilterBlocks = curFilterMgr.fFilterBlocks;
      mockFilterMgr.fFilterList = curFilterMgr.CloneFilterList();
      mockDoc.ApplyChartFilters(false);

      // Apply Highlights
      const curHighlightMgr = curDoc.GetHighlightMgr();
      const curHighlightList = curHighlightMgr?.GetAllowedHighlightList();
      const curHighlightInteractiveList = curHighlightMgr?.GetInteractiveHighlights();
      if (curHighlightList) {
        const mockHighlightMgr = mockDoc.GetHighlightMgr();
        mockHighlightMgr?.SyncHighlights(curHighlightList);

        mockHighlightMgr?.SetInteractiveHighlights(curHighlightInteractiveList);
        mockDoc.ApplyChartHighlights();
      }

      // Apply view rules to the mock before grouping — mockDoc.GetProxyTemplate()
      // may be null after JSON roundtrip on Custom Charts, fall back to curDoc.
      const previewTemplate = mockDoc.GetProxyTemplate?.() ?? curDoc.GetProxyTemplate?.() ?? null;
      if (previewTemplate) {
        ApplyChartTemplateToDocMgr(previewTemplate, wsMgr, null, mockDoc, false);
      }

      // Apply Grouping
      mockDoc.ApplyChartGroupingToDoc();

      function teardownMockDocument() {
        if (isNullish(wsMgr)) {
          return;
        }

        teardownTooManyResultsHandler();
        Globals._publishRef = null;
        wsMgr.CloseCanvas();
        wsMgr.fDocMgr = curDocMgr;
        wsMgr.SetPlanManager(curPlanMgr);
        resumePlanPolling();
        Globals._uiConfig.fShowOverlay = curShowOverlay;
        wsMgr.DropPanelReference(curCanvasPanel);
        wsMgr.AttachDocumentToUI(curDoc, false);
        curCanvasPanel?.SetRenderMode(enumPanelRenderMode.all);
        curViewMgr?.HandleUpdateBox();
        ApplyChartTemplateToDocMgr(curDoc.GetProxyTemplate(), wsMgr, null, curDoc, false);
        const curSC = curDoc.GetSubChartMgr()?.GetCurrentSubChart();
        if (curSC) curSC.SetNeedOptimize(true);
        void curCanvasPanel?.ReloadCurrentSubChart(false);
        wsMgr.BroadcastTopic("previewClosed", curDoc);
      }

      resolve([mockDoc, teardownMockDocument]);
    });
  }

  async exportDocument(options: DocumentExportOptions) {
    const wsMgr = this.wsMgr;
    const curDoc = wsMgr?.GetDoc();

    if (isNullish(curDoc) || isNullish(wsMgr)) {
      this.reject();
      return;
    }

    const { fieldData } = options;

    // Apply box sort config
    // const sortConfig = ValidateSortConfig(fieldData.sortConfig, mockDoc);
    // wsMgr.PerformSort(sortConfig, scWiz, false);

    // Write pub settings
    Globals._pubSettings.fWhiteBackground = fieldData.whiteBackground;
    Globals._pubSettings.fHideMaster = !fieldData.includeBackground;
    Globals._pubSettings.fShowActivity = fieldData.showActivity;
    Globals._pubSettings.fShowOverlay = fieldData.showOverlay;
    Globals._pubSettings.fDynNav = fieldData.navigationArrows;
    Globals._pubSettings.fUniformScaling = fieldData.uniformScaling;
    Globals._pubSettings.fDoIndex = fieldData.indexPage;
    Globals._pubSettings.fDoFunctionStatements =
      fieldData.functionStatements && !!wsMgr.fPermissions?.showFunctionalStatement();

    // Perform export
    const pubValue = ExportModalMode[options.mode].toUpperCase();
    const result = await wsMgr.PerformPublishCommand(pubValue, fieldData.scope, fieldData.name);

    this.resolve(result);
  }
}
